import { useMutation } from '@tanstack/react-query';
import { UpdateOrganizationCourtPayLaterEnabledDto } from 'models/organization/dto/organization.dto';
import { useApiClient } from '../../../apiClient';
import { useSelectedOrganization } from './useSelectedOrganization';

// Per-tenant gate for the court-booking "Pay later" option. Hits
// POST :id/court-pay-later-enabled on the API. The endpoint returns only the
// flipped flag ({ courtPayLaterEnabled }) — not the full aggregate — so we
// patch it into the cached selectedOrganization locally rather than blowing the
// whole org back into the Zustand store. Mirrors
// useUpdateOrganizationCoachingPayLaterEnabled in shape and intent.
export const useUpdateOrganizationCourtPayLaterEnabled = (
    organizationId: string,
    onSuccess: () => void,
) => {
    const apiClient = useApiClient();
    const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();

    return useMutation({
        mutationKey: ['updateOrganizationCourtPayLaterEnabled', organizationId],
        mutationFn: (body: UpdateOrganizationCourtPayLaterEnabledDto) =>
            apiClient.fetchApi<{ courtPayLaterEnabled: boolean }, UpdateOrganizationCourtPayLaterEnabledDto>({
                method: 'POST',
                endpoint: `organizations/${organizationId}/court-pay-later-enabled`,
                body,
            }),
        onSuccess: (data) => {
            onSuccess();
            // Only update the selected-org cache when the response is for the
            // org currently in view — defensive against the rare case where
            // the operator switches orgs while the mutation is in flight.
            if (selectedOrganization && organizationId === selectedOrganization.id) {
                setSelectedOrganization({
                    ...selectedOrganization,
                    courtPayLaterEnabled: data.courtPayLaterEnabled,
                });
            }
        },
    });
};
